import axios from 'axios';
import { API_BASE_URL, STORAGE_KEYS } from '../utils/authConstants';

// Create axios instance with interceptors
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  withCredentials: true, // Send cookies with requests for session management
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for token refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      try {
        const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
        const authMethod = localStorage.getItem(STORAGE_KEYS.AUTH_METHOD);
        
        if (refreshToken) {
          const refreshResponse = await refreshAuthToken(authMethod, refreshToken);
          
          if (refreshResponse.success) {
            localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, refreshResponse.access_token);
            originalRequest.headers.Authorization = `Bearer ${refreshResponse.access_token}`;
            return apiClient(originalRequest);
          }
        }
      } catch (refreshError) {
        // Refresh failed, logout user
        logout();
        window.location.href = '/login';
      }
    }
    
    return Promise.reject(error);
  }
);

// Auth utility functions
export const saveAuthData = (authData, method) => {
  localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, authData.access_token);
  localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, authData.refresh_token);
  localStorage.setItem(STORAGE_KEYS.AUTH_METHOD, method);
  if (authData.user) {
    localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(authData.user));
  }
};

export const clearAuthData = () => {
  localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.AUTH_METHOD);
  localStorage.removeItem(STORAGE_KEYS.USER_DATA);
};

export const getAuthData = () => {
  return {
    accessToken: localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN),
    refreshToken: localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN),
    authMethod: localStorage.getItem(STORAGE_KEYS.AUTH_METHOD),
    userData: JSON.parse(localStorage.getItem(STORAGE_KEYS.USER_DATA) || 'null')
  };
};

export const refreshAuthToken = async (method, refreshToken) => {
  const endpoints = {
    traditional: '/user/refresh-token',
    cognito: '/auth/cognito/refresh',
    oauth: '/auth/oauth/refresh'
  };
  
  const response = await apiClient.post(endpoints[method], {
    refresh_token: refreshToken
  });
  
  return response.data;
};

export const logout = async () => {
  const authMethod = localStorage.getItem(STORAGE_KEYS.AUTH_METHOD);
  
  try {
    const logoutEndpoints = {
      traditional: '/user/logout',
      cognito: '/auth/cognito/logout',
      oauth: '/auth/oauth/logout'
    };
    
    if (logoutEndpoints[authMethod]) {
      await apiClient.post(logoutEndpoints[authMethod]);
    }
  } catch (error) {
    console.error('Logout error:', error);
  } finally {
    clearAuthData();
  }
};

export default apiClient;